import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLOR = {
  in_progress: { background: '#fff3cd', color: '#856404' },
  submitted:   { background: '#cce5ff', color: '#004085' },
  approved:    { background: '#d4edda', color: '#155724' },
  rejected:    { background: '#f8d7da', color: '#721c24' },
};

export default function WorkQueue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    client.get('/api/tasks/')
      .then(res => setTasks(res.data))
      .catch(() => setError('Failed to load tasks.'))
      .finally(() => setLoading(false));
  }, []);

  const handleStart = async () => {
    setStarting(true);
    setError('');
    try {
      const res = await client.get('/api/tasks/next/');
      navigate(`/work/${res.data.task_id}`, { state: res.data });
    } catch (e) {
      if (e.response?.status === 404) {
        setError('No tasks available right now. Check back later.');
      } else {
        setError('Failed to fetch next task.');
      }
      setStarting(false);
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Work Queue</h1>
            <p style={styles.sub}>
              Welcome, <strong>{user?.name}</strong>
              {!loading && (
                <span style={styles.count}> · {tasks.length} task{tasks.length !== 1 ? 's' : ''} in history</span>
              )}
            </p>
          </div>
          <button style={styles.startBtn} onClick={handleStart} disabled={starting}>
            {starting ? 'Finding task…' : '▶ Start Labeling'}
          </button>
        </div>

        {error && <p style={styles.error}>{error}</p>}
        {loading && <p style={styles.hint}>Loading tasks…</p>}

        {!loading && tasks.length === 0 && (
          <p style={styles.hint}>No tasks in history yet. Click Start Labeling to begin.</p>
        )}

        {tasks.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Project', 'Image', 'Status', 'Assigned'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tasks.map(t => (
                  <tr key={t.id} style={styles.row}>
                    <td style={styles.td}>{t.project_name}</td>
                    <td style={styles.td}>{t.image_filename}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...(STATUS_COLOR[t.status] || {}) }}>
                        {t.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {t.assigned_at ? new Date(t.assigned_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'sans-serif' },
  content: { padding: '2rem', maxWidth: '960px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title: { margin: 0, fontSize: '1.6rem', color: '#2c3e50' },
  sub: { margin: '0.25rem 0 0', color: '#555' },
  count: { color: '#888', fontWeight: 400 },
  startBtn: {
    padding: '0.6rem 1.4rem', background: '#2c3e50', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  hint: { color: '#888' },
  error: { color: '#c0392b', marginBottom: '1rem' },
  tableWrap: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#2c3e50', color: '#fff', fontSize: '0.85rem' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '0.75rem 1rem', fontSize: '0.9rem', color: '#333' },
  badge: {
    display: 'inline-block', padding: '0.2rem 0.6rem',
    borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, textTransform: 'capitalize',
  },
};
